import { server } from '@api/request';
import type { License } from '@models';
import * as typeGuards from '@tools/type-guards';

/**
 * List all existing licenses.
 *
 * @returns  A list of licenses.
 */
export async function list() {
  return await server.list('v2/admin/licenses', processLicenseData);
}

type PaginatedLicenseResults = ZephyrWeb.Pagination.Results<License>;

export namespace LicenseSearch {
  /**
   * {@link search Search licenses} API request options.
   */
  export interface Options {
    licenseKey?: string;
    productId?: string;
    userId?: string;
    limit?: number;
    startKey?: ZephyrWeb.Pagination.PlacementKey;
  }

  /**
   * {@link search Search licenses} API request response.
   */
  export type Results = PaginatedLicenseResults;
}

/**
 * Search for licenses by a specified criteria (paginated).
 *
 * @param options Request options bag.
 * @returns `LicenseSearch.Results` data object.
 *
 * @see {@link ZephyrApi.Admin.Licenses.search `AdminSearchLicenses`}
 * API route in `zephyr-serverless`
 */
export async function search(options: LicenseSearch.Options) {
  return await server.post(
    'v2/admin/licenses/search',
    options,
    processSearchResults,
  );
}

/**
 * {@link listByOrganization List licenses by organization} API request options.
 */
export interface ListByOrganizationOptions {
  organizationId: string;
  assigned?: boolean;
}

/**
 * List all licenses for a specified organization.
 *
 * @param options Request options bag.
 * @returns A list of licenses.
 *
 * @see {@link ZephyrApi.Organizations.listLicenses `ListLicenses`} API
 * route in `zephyr-serverless`
 */
export async function listByOrganization(options: ListByOrganizationOptions) {
  const params: string[] = [];

  if (options.assigned) {
    params.push(`assigned=${options.assigned}`);
  }

  let url = `v2/organizations/${options.organizationId}/licenses`;

  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  return await server.list(url, processLicenseData);
}

/**
 * {@link create Create license} API request options.
 */
export interface CreateOptions {
  productId: License['productId'];
  amount: number;
  emails: string[];
  expiration?: License['expiration'];
}

/**
 * Create a new license.
 *
 * @param options Request options bag.
 * @returns The created license.
 *
 * @see {@link ZephyrApi.Organizations.addLicenses `CreateLicense`} API
 * route in `zephyr-serverless`
 */
export async function create(options: CreateOptions) {
  await server.post('v2/admin/licenses', options);
}

/**
 * {@link setState Set license state} API request options.
 */
export interface SetStateOptions {
  licenseId: License['id'];
  active: boolean;
}

/**
 * Set the state of a license.
 *
 * @param options Request options bag.
 * @returns The updated license.
 *
 * @see {@link ZephyrApi.Admin.Licenses.setState `AdminSetLicenseState`} API
 * route in `zephyr-serverless`
 */
export async function setState(options: SetStateOptions) {
  const { licenseId, ...data } = options;

  await server.post(`v2/admin/licenses/${licenseId}`, data);
}

/**
 * {@link redeem Redeem license} API request options.
 */
export interface RedeemOptions {
  key: string;
}

/**
 * Redeem a license using a valid license key.
 *
 * @param options Request options bag.
 *
 * @see {@link ZephyrApi.Licenses.redeem `RedeemLicense`} API
 * route in `zephyr-serverless`
 * @returns  ...
 */
export async function redeem(options: RedeemOptions) {
  await server.post('v2/licenses/redeem', options);
}

/**
 * {@link assign Assign license} API request options.
 */
export interface AssignOptions {
  organizationId: string;
  userId: string;
  licenseKey: string;
}

/**
 * Assign a license to a user.
 *
 * @param options Request options bag.
 * @returns The assigned license.
 *
 * @see {@link ZephyrApi.Organizations.assignLicense `AssignLicenses`} API
 * route in `zephyr-serverless`
 */
export async function assign(options: AssignOptions) {
  const { organizationId, ...data } = options;

  return await server.post(
    `v2/organizations/${organizationId}/assign-license`,
    data,
    processLicenseData,
  );
}

/**
 * {@link unassign Unassign license} API request options.
 */
export interface UnassignOptions {
  organizationId: string;
  licenseIds: string[];
}

/**
 * Unassign a license from a user.
 *
 * @param options Request options bag.
 *
 * @see {@link ZephyrApi.Organizations.unassignLicenses `UnassignLicenses`} API
 * route in `zephyr-serverless`
 */
export async function unassign(options: UnassignOptions) {
  await server.post(
    `v2/organizations/${options.organizationId}/unassign-licenses`,
    { licenses: options.licenseIds },
  );
}

//#region Helper Functions

/**
 * Determine if a value is a valid {@link License}.
 *
 * @param value The value to check.
 * @returns `true` if the value is a valid {@link License}, otherwise `false`.
 */
function isValidLicenseData(value: unknown): value is License {
  if (!typeGuards.isObject(value)) return false;

  return true;

  // return (
  //   typeGuards.isObject(value) &&
  //   typeGuards.isString(value.id) &&
  //   typeGuards.isString(value.productId) &&
  //   typeGuards.isString(value.userId) &&
  //   typeGuards.isString(value.key) &&
  //   typeGuards.isBoolean(value.active) &&
  //   typeGuards.isBoolean(value.preGenerated) &&
  //   typeGuards.isString(value.expiration) &&
  //   typeGuards.isString(value.assignedDate) &&
  //   typeGuards.isString(value.removeDate) &&
  //   typeGuards.isString(value.role) &&
  //   typeGuards.isString(value.owner)
  // );
}

/**
 * Process data received from a request expected to be a {@link License}.
 *
 * @param data Data received from the request.
 * @returns The processed data value.
 */
function processLicenseData(data: unknown) {
  if (!isValidLicenseData(data)) {
    throw new Error('Invalid license data item.');
  }

  return { ...data } as License;
}

/**
 * Determine if a value is a valid {@link LicenseSearch.Results}.
 *
 * @param value The value to check.
 * @returns `true` if the value is a valid {@link LicenseSearch.Results},
 * otherwise `false`.
 */
function isValidSearchResults(value: unknown): value is LicenseSearch.Results {
  return (
    typeGuards.isObject(value) &&
    typeGuards.isArray(value.items) &&
    (typeGuards.isObject(value.lastEvaluatedKey) ||
      typeGuards.isNull(value.lastEvaluatedKey))
  );
}

/**
 * Process data received from a request expected to be a
 * {@link LicenseSearch.Results}.
 *
 * @param data Data received from the request.
 * @returns The processed data value.
 */
function processSearchResults(data: unknown) {
  if (!isValidSearchResults(data)) {
    throw new Error('Invalid user search result data.');
  }

  return data;
}

//#endregion Helper Functions
